import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { uploadMedia, getEventMedia, getCurrentUser } from '../../services/supabase';
import MediaGallery from './MediaGallery';

const UploaderContainer = styled.div`
  margin-bottom: 1rem;
`;

const DropZone = styled.div`
  border: 2px dashed ${props => props.isDragActive ? '#2D882D' : '#ddd'};
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s;
  background-color: ${props => props.isDragActive ? 'rgba(45, 136, 45, 0.05)' : '#f9f9f9'};
  margin-bottom: 1.5rem;
  
  &:hover {
    border-color: #2D882D;
  }
`;

const formatDateForInput = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

const MediaUploader = ({ eventId }) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formData, setFormData] = useState({
    eventName: '',
    eventDate: formatDateForInput(),
    eventLocation: '',
    notes: ''
  });
  const fileInputRef = useRef(null);
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = getCurrentUser();
  const userId = user?.id;


  // Add useEffect to fetch media when component mounts or eventId changes
  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const { data, error } = await getEventMedia(eventId);
        if (error) throw error;
        if (data) {
          setUploads(data);
        }
      } catch (error) {
        console.error('Error fetching media:', error);
        setError('Failed to load media');
      }
    };

    if (eventId) {
      fetchMedia();
    }
  }, [eventId]); // Re-fetch when eventId changes

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length) {
      const file = e.dataTransfer.files[0];
      
      // Check if file is an image or video
      if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
        setSelectedFile(file);
      } else {
        alert('Please upload only images or videos');
      }
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setError(null);
    // console.log("uploading files", files);

    try {
      const uploadPromises = files.map(file => uploadMedia(file, eventId, userId));
      const results = await Promise.all(uploadPromises);
      // console.log("results", results);

      // Filter out any failed uploads and properly map the results
      const successfulUploads = results
        .filter(result => !result.error)
        .map(result => ({
          ...result.data,
          publicUrl: result.publicUrl
        }));

      // Update uploads with new media
      setUploads(prevUploads => [...successfulUploads, ...prevUploads]);

      // If there were any errors, show them
      const failedUploads = results.filter(result => result.error);
      if (failedUploads.length > 0) {
        const errorMessages = failedUploads
          .map(result => result.error.message)
          .filter(Boolean)
          .join('\n');
        setError(errorMessages || 'Some files failed to upload');
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      setError('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleDelete = (mediaId) => {
    setUploads(prevUploads => prevUploads.filter(upload => upload.id !== mediaId));
  };

  return (
    <div>
      <UploaderContainer>
        <DropZone
          onClick={handleClick}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <p style={{ marginBottom: '0' }}>Click or drag & drop to upload</p>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*,video/*"
            style={{ display: 'none' }}
            multiple
            disabled={uploading}
          />
        </DropZone>
      </UploaderContainer>
      {error && <div style={{ color: 'red' }}>{error}</div>}
      {uploading && <div>Uploading...</div>}
      <MediaGallery 
        uploads={uploads} 
        onDelete={handleDelete}
      />
    </div>
  );
};

export default MediaUploader; 